import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Text,
  Avatar,
  useDisclosure,
  IconButton,
  useBreakpointValue,
  Alert,
  AlertIcon,
  AlertTitle,
} from "@chakra-ui/react";
import { PiStethoscopeBold } from "react-icons/pi";
import {
  useAppointmentSlotsQuery,
  useCalendarQuery,
  useBannedPatientsLazyQuery,
} from "../graphql/types-and-hooks";
import { useBookAppointmentStore } from "../store";
import queryString from "query-string";
import { DateTime } from "luxon";
import { TimePicker, TimePickerDateTime } from "./TimePicker";
import { dateDifferenceFromNow } from "../utils/dates";
import { logError } from "./logger";
import { appointmentTypesStore } from "../store/AppointmentTypesStore";
import { calculateAgeFromDOB } from "../utils/dates";
import { isAgeInRange } from "../utils/isAgeInRange";
import { sessionManager } from "../managers/SessionManager";
import { useUserQuery } from "../graphql/types-and-hooks";
import { bookingFlowStore } from "../store/BookingFlowStore";
import CardSkeleton from "./CardSkeleton";
import { formatDate, formatTime, convertAppointment, toISOString } from "../lib/util"; // Import utility functions
import { getClinicV2 } from "../services/ClinicService";
import { clinicStore } from "../store/ClinicStore";
import { doctorsStore } from "../store/DoctorsStore";
import logger from "../utils/logger";

const INITIAL_STATE: TimePickerDateTime = {
  date: "", // Example date in ISO format (YYYY-MM-DD)
  formattedDate: "", // Example formatted date string
  time: "", // Example time (24-hour format)
  formattedTime: "", // Example formatted time string
  duration: "", // Example duration string
};

// eslint-disable-next-line no-restricted-globals
const parsed = queryString.parse(location.search);

// Access environment variables
const USER_FK = parsed.cid;
const CLINIC_GROUP_CRM = parsed.gid;
const LOCATION_ID = parsed.lid;
//const APPT_ID = parsed.aid;

interface Doctor {
  display_name: string;
  about: string;
  image: string;
  gender: string;
  doctor_id: string;
  my_patients: string;
  display_only: string;
  no_new_patients: string;
}

interface dateandtime {
  fadate: string;
  fatime: string;
}

interface DoctorCardProps {
  doctor: Doctor;
  ddate: dateandtime;
  duration: string;
  onBook?: () => void;
}

const DoctorCard: React.FC<DoctorCardProps> = ({
  doctor,
  ddate,
  duration,
  onBook,
}) => {
  const { display_name, image, doctor_id } = doctor;
  const { fadate, fatime } = ddate;
  // eslint-disable-next-line
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();

  const [
    bannedPatientsQuery,
    // eslint-disable-next-line
    { loading: bannedPatientsLoading },
  ] = useBannedPatientsLazyQuery();

  // eslint-disable-next-line
  const [patient, doctor1, serviceType, appointmentType, setData] =
    useBookAppointmentStore((state) => [
      state.patient,
      state.doctor,
      state.serviceType,
      state.appointmentType,
      state.setData,
      state.resetData,
    ]);

  const [startDate, setStartDate] = useState<DateTime>(null);

  const numberOfColumns = useBreakpointValue({ base: 2, md: 3, lg: 7 });
  // eslint-disable-next-line
  const [loadTimePicker, setLoadTimePicker] = useState(true);

  const endDate = useMemo(() => {
    if (!startDate) return null;

    return startDate.plus({ days: numberOfColumns - 1 });
  }, [startDate, numberOfColumns]);

  // eslint-disable-next-line
  const {} = useCalendarQuery({
    variables: {
      input: {
        clinic_group_crm: CLINIC_GROUP_CRM,
        user_fk: USER_FK,
        location_id: LOCATION_ID,
        doctor_id: doctor_id,
        year: DateTime.now().year.toString(),
        month: DateTime.now().month.toString(),
      },
    },
    skip: !loadTimePicker,
    onCompleted({ calendar }) {
      setStartDate(
        calendar?.first_available?.date
          ? DateTime.fromISO(calendar?.first_available?.date)
          : DateTime.now()
      );
      logError.info("Calendar query completed", { calendar });
    },
    onError(error) {
      logError.error("Calendar query error", error);
    },
  });

  const { data: appointmentSlotsData, loading: appointmentSlotsLoading } =
    useAppointmentSlotsQuery({
      variables: {
        input: {
          clinic_group_crm: CLINIC_GROUP_CRM,
          user_fk: USER_FK,
          location_id: LOCATION_ID,
          doctor_id: doctor_id,
          appointment_type_id:
            appointmentTypesStore.selectedAptType?.appointment_type_id,
          start_date: startDate,
          end_date: endDate,
        },
      },
      skip: !startDate || !endDate || !loadTimePicker,
      onCompleted(data) {
        logError.info("Appointment slots query completed", { data });
      },
      onError(error) {
        logError.error("Appointment slots query error", error);
      },
    });

  
  const [year, month, day] = fadate.split("-");
  const [hours, minutes] = fatime.split(":");

  const isoDateString = `${year}-${month}-${day}T${hours}:${minutes}:00`;

  const dateObj = new Date(isoDateString);

  const dateInput = dateObj;

  const formattedDate = formatDate(toISOString(dateInput)); // date

  const [isBanned, setIsBanned] = useState(false);
  const [isAgeValid, setIsAgeValid] = useState(true);

  const validatePatient = useCallback(async () => {
    if (!patient) return;
    try {
      const result = await bannedPatientsQuery({
        variables: {
          input: {
            clinic_group_crm: CLINIC_GROUP_CRM,
            user_fk: USER_FK,
            location_id: LOCATION_ID,
            patient_id: sessionManager.patientId,
          },
        },
      });

      if (sessionManager.patientId) {
        const bannedLevel = parseInt(
          result?.data?.bannedPatients.banned_level || "0"
        );
        if (bannedLevel !== 0) {
          setIsBanned(true);
          return;
        }
      }

      const patientAge = calculateAgeFromDOB(patient.dob);

      if (
        !isAgeInRange(patientAge, {
          low_age_min: appointmentTypesStore.selectedAptType?.low_age_min,
          low_age_max: appointmentTypesStore.selectedAptType?.low_age_max,
          upper_age_min: appointmentTypesStore.selectedAptType?.upper_age_min,
          upper_age_max: appointmentTypesStore.selectedAptType?.upper_age_max,
        })
      ) {
        setIsAgeValid(false);
        return;
      }
    } catch (error) {
      logError("Error validating patient", error);
      console.error("Validation Error:", error);
    }
  }, [bannedPatientsQuery, patient]);

  const formatDateToISO = (dateString: string) => {
    const [day, month, year] = dateString.split("-");
    return `${year}-${month}-${day}`;
  };

  const [selectedDateTime, setSelectedDateTime] = useState(INITIAL_STATE);

  // eslint-disable-next-line
  const [selectedTime, setSelectedTime] = useState<{
    formattedTime: string;
    date: string;
    time: string;
    duration: string;
  } | null>(null);

  const formattedDateISO = formatDateToISO(fadate);

  const handleTimeClick = () => {
    if (onBook) {
      onBook();
      return;
    }
    const newSelectedTime = {
      date: fadate,
      formattedDate: formattedDateISO, // formatted date
      formattedTime: formatTime(parseInt(hours, 10), parseInt(minutes, 10)),
      time: fatime,
      duration: duration,
    };

    console.log(
      "Converted newSelectedTime ",
      convertAppointment(newSelectedTime)
    );

    const convertedSelectedTime = convertAppointment(newSelectedTime);
    console.log("Selected Time:", newSelectedTime);

    setSelectedTime(convertedSelectedTime);
    setData({ doctor: doctor });

    bookingFlowStore.updateAppointmentData("serviceType", serviceType);
    bookingFlowStore.updateAppointmentData(
      "appointmentType",
      appointmentTypesStore.selectedAptType
    );
    bookingFlowStore.updateAppointmentData("selectedDoctor", doctor);
    bookingFlowStore.updateAppointmentData(
      "selectedTime",
      convertedSelectedTime
    );

    bookingFlowStore.goToStep(8);
    bookingFlowStore.setActiveStep(8);
  };

  const handlePrevDates = () => {
    setSelectedDateTime(INITIAL_STATE);
    setStartDate((prevState) => prevState.minus({ days: numberOfColumns }));
    logError.info("Previous dates clicked");
  };

  const handleNextDates = () => {
    setSelectedDateTime(INITIAL_STATE);
    setStartDate((prevState) => prevState.plus({ days: numberOfColumns }));
    logError.info("Next dates clicked");
  };

  const { data: userData } = useUserQuery({
    variables: {},
  });

  const user = userData?.user;
  // eslint-disable-next-line
  const isLoggedIn = user;

  const handleSelectTime = async (time) => {
    const newSelectedTime = {
      formattedDate: time.formattedDate,
      formattedTime: time.formattedTime,
      date: time.date,
      time: time.time,
      duration: time.duration,
    };

    logger.log("Selected Time:", newSelectedTime);

    bookingFlowStore.updateAppointmentData("selectedTime", newSelectedTime);
    doctorsStore.setCurrentDoctor(doctor);

    const clinicData = await getClinicV2(CLINIC_GROUP_CRM, USER_FK.toString(), LOCATION_ID.toString());
    const currentClinic = Array.isArray(clinicData) && clinicData.length > 0 ? clinicData[0] : null;
    if (currentClinic) {
      clinicStore.setCurrentClinic(currentClinic);
    }

    logger.log("Clinic Data:", clinicStore.currentClinic);
    logger.log("Selected Doctor:", doctorsStore.selectedDoctor);

    bookingFlowStore.goToStep(8);
    bookingFlowStore.setActiveStep(8);
  };

  const handleSelectDoctor = (doctor) => {
    bookingFlowStore.updateAppointmentData("selectedDoctor", doctor);
    setData({ doctor });
  };

  useEffect(() => {
    if (isOpen) {
      const height = "1500px";
      window.parent.postMessage({ type: "addsizeIframe", height }, "*");
    }
  }, [isOpen]);

  useEffect(() => {
    validatePatient();
  }, [validatePatient]);

  const isValidDate = (date) => {
    if (!date) {
      return false;
    }

    const parsedDate = new Date(date);

    if (!isNaN(parsedDate.getTime())) {
      return false;
    }
    const hardcodedInvalidDate = new Date("2000-01-01T00:00:00");
    if (parsedDate.getTime() === hardcodedInvalidDate.getTime()) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (parsedDate < today) {
      return false;
    }

    return true;
  };

  const myPatientsOnly = doctor?.my_patients === "1";
  const isExistingPatientOfDoctor =
    patient?.last_seen_did === doctor?.doctor_id;
  const displayOnly = doctor?.display_only === "1";

  const isNewPatient = (patient?.is_new_pt ?? "1") === "1";

  const isFormattedDateValid =
    isValidDate(formattedDate) &&
    formattedDate !== "12.00 am Sat 1st Jan 2000" &&
    !formattedDate.includes("NaN") &&
    !formattedDate.includes("undefined");

  const allowBooking = !(
    !isFormattedDateValid ||
    isBanned ||
    !isAgeValid ||
    ((appointmentTypesStore.selectedAptType?.new_patient ?? "0") === "0" &&
      isNewPatient) ||
    (doctor?.no_new_patients === "1" && isNewPatient) ||
    (doctor?.my_patients === "1" && !isExistingPatientOfDoctor)
  );

  const renderTimePicker = () => {
    if (appointmentSlotsLoading) {
      return <CardSkeleton />;
    }

    if (!isExistingPatientOfDoctor && myPatientsOnly) {
      return (
        <Alert status="info">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>
              {`Dr. ${doctor?.display_name} is available for excisting patients only`}
            </AlertTitle>
          </Box>
        </Alert>
      );
    }

    if (displayOnly) {
      return (
        <Alert status="info">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>
              {`Dr. ${doctor?.display_name} is available for existing patients only`}
            </AlertTitle>
          </Box>
        </Alert>
      );
    }

    if (!isFormattedDateValid) {
      return null;
    }

    if (!appointmentSlotsData) return null;

    return (
      <TimePicker
        slots={appointmentSlotsData.appointmentSlots}
        value={selectedDateTime}
        onChange={(time) => {
          setSelectedDateTime(time);
          handleSelectDoctor(doctor);
          handleSelectTime(time);
        }}
        onPrev={handlePrevDates}
        onNext={handleNextDates}
        showPrev={dateDifferenceFromNow(startDate)?.days >= 3}
      />
    );
  };

  return (
    <Box
      borderWidth="2px"
      borderRadius="xl"
      boxShadow="md"
      display="flex"
      flexDirection="row"
      justifyContent={"space-between"}
      mt={3}
      p={[2, 4, 4, 4, 4]}
    >
      <Box width={"100%"}>
        <Box
          display="flex"
          flexDirection={["column", "column", "row"]}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <Box
            pb={{ base: 4, md: 0 }}
            display="flex"
            alignItems="center"
            flexDirection={["row"]}
            cursor="pointer"
          >
            <Avatar
              name={display_name}
              size="lg"
              src={image}
              style={{ boxShadow: "5px 5px 5px #bbb" }}
            ></Avatar>
            <Box ml={4}>
              <Text fontWeight="bold">{display_name}</Text>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection={["column", "column", "row"]}
            gap={"10px"}
          >
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              minWidth={"265px"}
              justifyContent={"space-between"}
            >
              <Box
                borderRadius={"full"}
                height={"50px"}
                display="flex"
                alignItems="center"
                background={"#B92F2F"}
                color={"#FFFFFF"}
                flexDirection={"row"}
                width={"100%"}
                onClick={handleTimeClick}
                cursor="pointer"
                padding={1}
              >
                <IconButton
                  isRound={true}
                  onClick={handleTimeClick}
                  variant="solid"
                  color="#B92F2F"
                  background={"#FFFFFF"}
                  aria-label="Select Time"
                  fontSize="20px"
                  icon={<PiStethoscopeBold />}
                  _hover={allowBooking ? { bg: "#B92F2F", color: "white" } : {}}
                  isDisabled={!allowBooking}
                />

                <Box
                  pl="10px"
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  height="100%"
                  width="100%"
                >
                  <Text fontSize="sm" fontWeight={"bold"} color="#FFFFFF">
                    Book first available time{" "}
                  </Text>
                  <Text
                    fontSize="xs"
                    fontWeight={"bold"}
                    mt="-3px"
                    color="#FFFFFF"
                  >
                    {formattedDate || ""}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <>
          <Box>{renderTimePicker()}</Box>
        </>
      </Box>
    </Box>
  );
};

export default DoctorCard;
